import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/endPoints";
import { User } from "../../types/auth.types";

interface AuthResponse {
  user?: User;
  error?: string;
  type: "AUTH_SUCCESS" | "AUTH_ERROR";
}

// Derive allowed origins for postMessage validation
const getAllowedOrigins = (): string[] => {
  const origins: string[] = [];
  const media = process.env.REACT_APP_MEDIA_URL;
  if (media) origins.push(media);
  const api = process.env.REACT_APP_API_URL;
  try {
    if (api) origins.push(new URL(api).origin);
  } catch {}
  // Deduplicate
  return Array.from(new Set(origins.filter(Boolean)));
};

export const initiateGoogleLogin = (
  options?: { forcePrompt?: boolean }
): Promise<User> => {
  return new Promise<User>((resolve, reject) => {
    // So developers can test without Google OAuth
    const developer = process.env.REACT_APP_USER;
    if (developer) {
      const dev = JSON.parse(developer);
      if (dev) {
        resolve(dev);
        return;
      }
    }
    // Configure popup window dimensions
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    console.log("App role requested is:", process.env.REACT_APP_ROLE);
    // Open the popup window for Google OAuth with role parameter
    const baseUrl = `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.AUTH.GOOGLE_LOGIN}`;
    const params = new URLSearchParams({
      role: String(process.env.REACT_APP_ROLE || "")
    });

    // If we need to force Google to show the account chooser and re-auth
    if (options?.forcePrompt) {
      // These are standard OIDC/Google hints; backend may choose to forward them
      params.set("prompt", "select_account consent");
      params.set("max_age", "0");
    }

    // Add a simple cache buster to avoid any intermediary caching
    params.set("_", String(Date.now()));

    const url = `${baseUrl}?${params.toString()}`;

    const popup = window.open(
      url,
      "Google Login",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Listen for the authentication response from the popup
    function authMessageListener(event: MessageEvent<AuthResponse>) {
      // Security check: ensure message is from our backend
      const allowedOrigins = getAllowedOrigins();
      if (!allowedOrigins.includes(event.origin)) return;

      // If the backend provided a user payload, persist the email for diagnostics/UI
      const attemptedEmail = event.data?.user?.email;
      if (attemptedEmail) {
        try { localStorage.setItem("lastLoginEmail", attemptedEmail); } catch {}
      }

      // Handle the authentication response
      if (event.data.type === "AUTH_SUCCESS" && event.data.user) {
        localStorage.setItem("googleToken", JSON.stringify(event.data.user));
        localStorage.setItem("tokenExpiry", (Date.now() + 3600000).toString());
        resolve(event.data.user);
      } else if (event.data.type === "AUTH_ERROR") {
        reject(new Error(event.data.error || "Authentication failed"));
      }

      // Clean up: remove listener and close popup
      window.removeEventListener("message", authMessageListener);
      if (popup) popup.close();
    }

    // Attach the listener to receive the message from the popup
    window.addEventListener("message", authMessageListener);

    // Handle case where popup is blocked or closed
    const popupCheckInterval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(popupCheckInterval);
        window.removeEventListener("message", authMessageListener);
        reject(new Error("Authentication cancelled"));
      }
    }, 1000);
  });
};

export const checkAuthStatus = async (): Promise<User | null> => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.AUTH.CHECK_AUTH);
    // Clear any previous auth error status on success
    localStorage.removeItem("lastAuthHttpStatus");
    return response.data;
  } catch (error: any) {
    console.error("Error checking auth status:", error);
    const status: number | undefined = error?.response?.status;
    if (status) {
      // Persist the last auth-related HTTP status so UI flows can tailor messaging
      localStorage.setItem("lastAuthHttpStatus", String(status));
    }
    return null;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    localStorage.removeItem("googleToken");
    localStorage.removeItem("tokenExpiry");
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};